import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, AlertTriangle, Package, Filter, ChevronLeft, ChevronRight, Truck, ShoppingCart, CheckCircle, Warehouse, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { PageHeader, LoadingState, EmptyStateNoResults } from '@/components/design-system';

const Inventory = () => {
  const { hasPermission, user } = useContext(AuthContext);
  const canViewCosts = hasPermission('costs.view');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  
  // Session storage key for filter persistence
  const FILTER_STORAGE_KEY = 'inventory_filters';
  
  // Load saved filters from sessionStorage
  const getSavedFilters = () => {
    try {
      const saved = sessionStorage.getItem(FILTER_STORAGE_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  };
  
  const savedFilters = getSavedFilters();
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(savedFilters?.currentPage || 1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [pageSize] = useState(50);
  
  // Filter state - restore from sessionStorage if available
  const [searchInput, setSearchInput] = useState(savedFilters?.searchTerm || ''); // User input (immediate)
  const [searchTerm, setSearchTerm] = useState(savedFilters?.searchTerm || ''); // Debounced value (triggers fetch)
  const [selectedCategory, setSelectedCategory] = useState(savedFilters?.selectedCategory || 'all');
  const [selectedSupplier, setSelectedSupplier] = useState(savedFilters?.selectedSupplier || 'all');
  const [stockStatus, setStockStatus] = useState(savedFilters?.stockStatus || 'all');
  const [articleType, setArticleType] = useState(savedFilters?.articleType || 'all');
  const [sortBy, setSortBy] = useState(savedFilters?.sortBy || 'sku');  // Default: alphabetical by SKU
  const [sortOrder, setSortOrder] = useState(savedFilters?.sortOrder || 'asc');
  // UX: por omissão esconder artigos sem stock para todas as consultas de stock.
  const [hideZeroStock, setHideZeroStock] = useState(savedFilters?.hideZeroStock ?? true);
  
  // Debounce search input - only update searchTerm after 350ms of no typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
      setCurrentPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);
  
  const [formData, setFormData] = useState({
    sku: '',
    name: '',
    description: '',
    category: '',
    current_stock: 0,
    reorder_point: '',
    optimal_stock: '',
    moq: '',
    unit_price: 0,
    supplier: ''
  });
  
  // Warehouse filter state - initialize with saved value, or user's default warehouse if set
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouses, setSelectedWarehouses] = useState(() => {
    if (savedFilters?.selectedWarehouses) {
      return savedFilters.selectedWarehouses;
    }
    // Pre-select user's reference warehouse (canonical) with legacy fallback
    const refWh = user?.reference_warehouse || user?.default_warehouse;
    if (refWh) {
      return [refWh];
    }
    return ['all'];
  });
  const [showWarehouseFilter, setShowWarehouseFilter] = useState(false);

  // Save filters to sessionStorage whenever they change
  useEffect(() => {
    const filters = {
      currentPage,
      searchTerm: searchInput, // Save the visible input value, not the debounced
      selectedCategory,
      selectedSupplier,
      stockStatus,
      articleType,
      sortBy,
      sortOrder,
      hideZeroStock,
      selectedWarehouses,
    };
    sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
  }, [currentPage, searchInput, selectedCategory, selectedSupplier, stockStatus, articleType, sortBy, sortOrder, hideZeroStock, selectedWarehouses]);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [currentPage, searchTerm, selectedCategory, selectedSupplier, stockStatus, articleType, sortBy, sortOrder, hideZeroStock, selectedWarehouses]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/products/categories`);
      setCategories(response.data.categories);
      setSuppliers(response.data.suppliers);
    } catch (error) {
      console.error('Failed to load categories');
    }
    
    // Fetch available warehouses
    try {
      const whRes = await axios.get(`${API}/warehouses`);
      if (whRes.data && whRes.data.warehouses && Array.isArray(whRes.data.warehouses)) {
        setWarehouses(whRes.data.warehouses);
      } else if (whRes.data && Array.isArray(whRes.data)) {
        setWarehouses(whRes.data);
      }
    } catch (error) {
      console.error('Failed to load warehouses');
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        page_size: pageSize.toString(),
        sort_by: sortBy,
        sort_order: sortOrder,
        include_pending: 'true'  // Include pending orders info
      });
      
      if (searchTerm) params.append('search', searchTerm);
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      if (selectedSupplier !== 'all') params.append('supplier', selectedSupplier);
      if (stockStatus !== 'all') params.append('stock_status', stockStatus);
      if (articleType !== 'all') params.append('article_type', articleType);
      // Hide zero-stock items by default (UX-friendly stock view).
      // `stock_status === 'out'` overrides this (user explicitly asked for zero-stock).
      if (hideZeroStock && stockStatus !== 'out') params.append('hide_zero_stock', 'true');
      // Warehouse filter - if specific warehouses selected, pass them
      if (!selectedWarehouses.includes('all') && selectedWarehouses.length > 0) {
        params.append('warehouses', selectedWarehouses.join(','));
      }
      
      const response = await axios.get(`${API}/products?${params.toString()}`);
      setProducts(response.data.items);
      setTotalPages(response.data.total_pages);
      setTotalProducts(response.data.total);
    } catch (error) {
      toast.error('Falha ao carregar produtos');
    } finally {
      setLoading(false);
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    const toNum = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
    try {
      await axios.post(`${API}/products`, {
        ...formData,
        current_stock: Number(formData.current_stock),
        reorder_point: toNum(formData.reorder_point),
        optimal_stock: toNum(formData.optimal_stock),
        moq: toNum(formData.moq),
        unit_price: Number(formData.unit_price)
      });
      toast.success('Produto adicionado com sucesso');
      setShowAddModal(false);
      setFormData({
        sku: '',
        name: '',
        description: '',
        category: '',
        current_stock: 0,
        reorder_point: '',
        optimal_stock: '',
        moq: '',
        unit_price: 0,
        supplier: ''
      });
      fetchProducts();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Falha ao adicionar produto');
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Tem a certeza que deseja eliminar este produto?')) return;

    try {
      await axios.delete(`${API}/products/${productId}`);
      toast.success('Produto eliminado com sucesso');
      fetchProducts();
    } catch (error) {
      toast.error('Falha ao eliminar produto');
    }
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setSelectedCategory('all');
    setSelectedSupplier('all');
    setStockStatus('all');
    setHideZeroStock(true);
    setSelectedWarehouses(['all']);
    setShowWarehouseFilter(false);
    setCurrentPage(1);
    // Clear saved filters from sessionStorage
    sessionStorage.removeItem(FILTER_STORAGE_KEY);
  };

  const getStockStatus = (product) => {
    const rp = product.reorder_point ?? product.manual_reorder_point;
    if (product.current_stock === 0) {
      return { label: 'Sem Stock', color: 'text-red-500', bgColor: 'bg-red-100' };
    } else if (rp != null && product.current_stock <= rp) {
      return { label: 'Stock Baixo', color: 'text-orange-500', bgColor: 'bg-orange-100' };
    } else if (rp == null) {
      return { label: 'Não configurado', color: 'text-slate-500', bgColor: 'bg-slate-100' };
    }
    return { label: 'Em Stock', color: 'text-emerald-500', bgColor: 'bg-emerald-100' };
  };

  if (loading && products.length === 0) {
    return <LoadingState message="A carregar inventário..." />;
  }

  return (
    <div className="p-4 sm:p-6 max-w-[1600px] mx-auto" data-testid="inventory-page">
      {/* Header */}
      <PageHeader
        title="Inventário"
        subtitle={`${totalProducts.toLocaleString()} produtos · Pág. ${currentPage}/${totalPages}`}
        actions={
          <Button 
            onClick={() => setShowAddModal(true)}
            data-testid="add-product-button" 
            className="bg-slate-800 hover:bg-slate-700 whitespace-nowrap flex-shrink-0"
          >
            <Plus className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Adicionar Produto</span>
          </Button>
        }
      />
      
      {/* Add Product Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="sm:max-w-[600px] bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Adicionar Novo Produto</DialogTitle>
          </DialogHeader>
            <form onSubmit={handleAddProduct} className="space-y-4 mt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sku">SKU *</Label>
                  <Input
                    id="sku"
                    data-testid="product-sku-input"
                    value={formData.sku}
                    onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Nome do Produto *</Label>
                  <Input
                    id="name"
                    data-testid="product-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Descrição</Label>
                <Input
                  id="description"
                  data-testid="product-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Categoria</Label>
                  <Input
                    id="category"
                    data-testid="product-category-input"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="supplier">Fornecedor</Label>
                  <Input
                    id="supplier"
                    data-testid="product-supplier-input"
                    value={formData.supplier}
                    onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="current_stock">Stock Atual</Label>
                  <Input
                    id="current_stock"
                    data-testid="product-stock-input"
                    type="number"
                    value={formData.current_stock}
                    onChange={(e) => setFormData({ ...formData, current_stock: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="unit_price">Preço Unitário (€)</Label>
                  <Input
                    id="unit_price"
                    data-testid="product-price-input"
                    type="number"
                    step="0.01"
                    value={formData.unit_price}
                    onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="reorder_point">Ponto de Reencomenda</Label>
                  <Input
                    id="reorder_point"
                    data-testid="product-reorder-point-input"
                    type="number"
                    value={formData.reorder_point}
                    onChange={(e) => setFormData({ ...formData, reorder_point: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reorder_quantity">Quantidade de Reencomenda</Label>
                  <Input
                    id="reorder_quantity"
                    data-testid="product-reorder-quantity-input"
                    type="number"
                    value={formData.reorder_quantity}
                    onChange={(e) => setFormData({ ...formData, reorder_quantity: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" data-testid="submit-product-button" className="flex-1 bg-red-600 hover:bg-red-700">
                  Adicionar Produto
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1"
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

      {/* Filters */}
      <Card className="border border-slate-200 rounded-lg shadow-sm mb-4 sm:mb-6">
        <CardContent className="p-3 sm:p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 sm:gap-4">
            {/* Search */}
            <div className="sm:col-span-2 lg:col-span-2">
              <Label className="text-xs text-slate-500 mb-1.5 block">Pesquisa</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  data-testid="search-products-input"
                  placeholder="SKU, nome, descrição..."
                  className="pl-10"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
            </div>

            {/* Category Filter */}
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Família</Label>
              <Select value={selectedCategory} onValueChange={(value) => { setSelectedCategory(value); setCurrentPage(1); }}>
                <SelectTrigger data-testid="category-filter">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-[300px]">
                  <SelectItem value="all">Todas</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Supplier Filter */}
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Fornecedor</Label>
              <Select value={selectedSupplier} onValueChange={(value) => { setSelectedSupplier(value); setCurrentPage(1); }}>
                <SelectTrigger data-testid="supplier-filter">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-[300px]">
                  <SelectItem value="all">Todos</SelectItem>
                  {suppliers.map((sup) => (
                    <SelectItem key={sup} value={sup}>{sup}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Stock Status Filter */}
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Estado Stock</Label>
              <Select value={stockStatus} onValueChange={(value) => { setStockStatus(value); setCurrentPage(1); }}>
                <SelectTrigger data-testid="stock-status-filter">
                  <SelectValue placeholder="Todo" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">Todo</SelectItem>
                  <SelectItem value="normal">Em Stock</SelectItem>
                  <SelectItem value="low">Baixo</SelectItem>
                  <SelectItem value="out">Sem Stock</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Article Type Filter (Artigo vs Descritor/Serviço) */}
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Tipo Artigo</Label>
              <Select value={articleType} onValueChange={(value) => { setArticleType(value); setCurrentPage(1); }}>
                <SelectTrigger data-testid="article-type-filter">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="product">Artigos</SelectItem>
                  <SelectItem value="service">Descritores/Serviços</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Warehouse Filter */}
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Armazém</Label>
              <div className="relative">
                <Button
                  variant="outline"
                  className="w-full justify-between h-10 font-normal"
                  onClick={() => setShowWarehouseFilter(!showWarehouseFilter)}
                  data-testid="warehouse-filter-btn"
                >
                  <span className="flex items-center gap-2">
                    <Warehouse className="h-4 w-4 text-slate-400" />
                    {selectedWarehouses.includes('all') 
                      ? 'Todos' 
                      : selectedWarehouses.length === 1 
                        ? selectedWarehouses[0]
                        : `${selectedWarehouses.length} selecionados`
                    }
                  </span>
                </Button>
                {showWarehouseFilter && (
                  <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg p-2">
                    <div className="space-y-1 max-h-[200px] overflow-y-auto">
                      <label className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer">
                        <Checkbox 
                          checked={selectedWarehouses.includes('all')}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedWarehouses(['all']);
                            }
                            setCurrentPage(1);
                          }}
                        />
                        <span className="text-sm font-medium">Todos os Armazéns</span>
                      </label>
                      {warehouses.map((wh) => (
                        <label key={wh.code} className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer">
                          <Checkbox 
                            checked={!selectedWarehouses.includes('all') && selectedWarehouses.includes(wh.code)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                const newSelection = selectedWarehouses.filter(w => w !== 'all');
                                setSelectedWarehouses([...newSelection, wh.code]);
                              } else {
                                const newSelection = selectedWarehouses.filter(w => w !== wh.code && w !== 'all');
                                setSelectedWarehouses(newSelection.length > 0 ? newSelection : ['all']);
                              }
                              setCurrentPage(1);
                            }}
                          />
                          <span className="text-sm">{wh.code} - {wh.name}</span>
                        </label>
                      ))}
                    </div>
                    <div className="border-t mt-2 pt-2">
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="w-full text-xs"
                        onClick={() => setShowWarehouseFilter(false)}
                      >
                        Fechar
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-slate-200">
            <div className="flex items-center gap-2 flex-wrap">
              <Filter className="h-4 w-4 text-muted-foreground hidden sm:block" />
              <span className="text-xs sm:text-sm text-muted-foreground">Ordenar:</span>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[120px] sm:w-[150px]" data-testid="sort-by-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="name">Nome</SelectItem>
                  <SelectItem value="sku">SKU</SelectItem>
                  <SelectItem value="current_stock">Stock</SelectItem>
                  <SelectItem value="unit_price">Preço</SelectItem>
                  <SelectItem value="updated_at">Atualização</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sortOrder} onValueChange={setSortOrder}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="asc">Ascendente</SelectItem>
                  <SelectItem value="desc">Descendente</SelectItem>
                </SelectContent>
              </Select>
              <label className="ml-2 inline-flex items-center gap-2 text-xs sm:text-sm text-slate-700 cursor-pointer select-none" data-testid="hide-zero-stock-toggle">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500 cursor-pointer"
                  checked={hideZeroStock}
                  onChange={(e) => { setHideZeroStock(e.target.checked); setCurrentPage(1); }}
                />
                <span>Esconder sem stock</span>
              </label>
            </div>
            <Button variant="outline" size="sm" onClick={handleResetFilters} data-testid="reset-filters">
              Limpar Filtros
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Products View - Cards for Mobile, Table for Desktop */}
      {products.length === 0 ? (
        <EmptyStateNoResults 
          searchTerm={searchTerm} 
          onClear={handleResetFilters}
        />
      ) : (
        <>
          {/* Mobile Cards View */}
          <div className="md:hidden space-y-2" data-testid="inventory-mobile-view">
            {products.map((product) => {
              const hasPending = (product.pending_quantity || 0) > 0 || (product.cart_quantity || 0) > 0;
              const virtualStock = product.virtual_stock ?? product.current_stock;
              const rp = product.reorder_point ?? product.manual_reorder_point;
              const isVirtualOk = rp != null && virtualStock >= rp;
              const productIdentifier = product.id || product.sku;
              
              let displayStock = product.current_stock;
              if (!selectedWarehouses.includes('all') && product.stock_by_warehouse) {
                displayStock = selectedWarehouses.reduce((sum, whCode) => {
                  return sum + (product.stock_by_warehouse[whCode] || 0);
                }, 0);
              }
              
              return (
                <Card key={productIdentifier} className="border border-slate-200" data-testid={`product-card-${product.sku}`}>
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <Link 
                          to={`/inventory/${productIdentifier}`}
                          className="text-sm font-medium text-slate-900 hover:text-red-700 line-clamp-2 leading-tight block"
                        >
                          {product.name}
                        </Link>
                        <p className="text-[11px] font-mono text-slate-400 mt-0.5">{product.sku}</p>
                      </div>
                      {/* Status Badge */}
                      {product.stock_status_label === 'ok' ? (
                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px] px-1.5 py-0.5 flex-shrink-0">
                          <CheckCircle className="h-3 w-3" />
                        </Badge>
                      ) : product.stock_status_label === 'pending_resolution' ? (
                        <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-[10px] px-1.5 py-0.5 flex-shrink-0">
                          <Truck className="h-3 w-3" />
                        </Badge>
                      ) : product.stock_status_label === 'out_of_stock' ? (
                        <Badge className="bg-red-100 text-red-700 border-red-200 text-[10px] px-1.5 py-0.5 flex-shrink-0">
                          <AlertTriangle className="h-3 w-3" />
                        </Badge>
                      ) : product.stock_status_label === 'unknown' ? (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 text-slate-500 border-slate-200 flex-shrink-0">
                          <span>—</span>
                        </Badge>
                      ) : (
                        <Badge className="bg-orange-100 text-orange-700 border-orange-200 text-[10px] px-1.5 py-0.5 flex-shrink-0">
                          <AlertTriangle className="h-3 w-3" />
                        </Badge>
                      )}
                    </div>
                    
                    {/* Stock info row */}
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                      <div className="flex items-center gap-3">
                        <div className="text-center">
                          <p className="text-[10px] text-slate-500 uppercase">Stock</p>
                          <p className={`font-mono font-bold text-sm ${displayStock <= 0 ? 'text-red-600' : 'text-slate-900'}`}>
                            {displayStock}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-[10px] text-slate-500 uppercase">Virtual</p>
                          <p className={`font-mono font-bold text-sm ${
                            isVirtualOk ? 'text-emerald-600' : 
                            virtualStock > 0 ? 'text-orange-600' : 'text-red-600'
                          }`}>
                            {virtualStock}
                          </p>
                        </div>
                        {hasPending && (
                          <div className="text-center">
                            <p className="text-[10px] text-slate-500 uppercase">Pend.</p>
                            <div className="flex flex-col items-center">
                              {product.pending_quantity > 0 && (
                                <span className="text-blue-600 font-mono text-xs font-medium flex items-center gap-0.5">
                                  <Truck className="h-3 w-3" />+{product.pending_quantity}
                                </span>
                              )}
                              {product.cart_quantity > 0 && (
                                <span className="text-purple-600 font-mono text-xs flex items-center gap-0.5">
                                  <ShoppingCart className="h-3 w-3" />+{product.cart_quantity}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        {canViewCosts && product.avg_cost_price != null && product.avg_cost_price > 0 && (
                          <div className="text-center">
                            <p className="text-[10px] text-slate-500 uppercase">Custo</p>
                            <p className="font-mono text-xs text-slate-700">€{Number(product.avg_cost_price).toFixed(2)}</p>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Link to={`/inventory/${productIdentifier}`}>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <Edit className="h-4 w-4" />
                          </Button>
                        </Link>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                          onClick={() => handleDelete(productIdentifier)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          
          {/* Desktop Table View */}
          <Card className="border border-slate-200 rounded-lg shadow-sm hidden md:block">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-xs lg:text-sm" style={{ tableLayout: 'fixed' }}>
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left py-3 px-3 font-semibold text-xs uppercase tracking-wider text-slate-600" style={{ width: '120px' }}>SKU</th>
                      <th className="text-left py-3 px-3 font-semibold text-xs uppercase tracking-wider text-slate-600" style={{ width: '280px', minWidth: '200px' }}>Nome</th>
                      <th className="text-left py-3 px-3 font-semibold text-xs uppercase tracking-wider text-slate-600 hidden xl:table-cell" style={{ width: '70px' }}>Família</th>
                      <th className="text-right py-3 px-3 font-semibold text-xs uppercase tracking-wider text-slate-600" style={{ width: '65px' }}>Stock</th>
                      <th className="text-right py-3 px-3 font-semibold text-xs uppercase tracking-wider text-slate-600" style={{ width: '70px' }}>Pend.</th>
                      <th className="text-right py-3 px-3 font-semibold text-xs uppercase tracking-wider text-slate-600" style={{ width: '65px' }}>Virtual</th>
                      <th className="text-right py-3 px-3 font-semibold text-xs uppercase tracking-wider text-slate-600 hidden xl:table-cell" style={{ width: '60px' }}>Reenc.</th>
                      {canViewCosts && (
                        <th className="text-right py-3 px-3 font-semibold text-xs uppercase tracking-wider text-slate-600" style={{ width: '85px' }} title="Custo Médio Ponderado (PCM)">Custo</th>
                      )}
                      <th className="text-left py-3 px-3 font-semibold text-xs uppercase tracking-wider text-slate-600 hidden xl:table-cell" style={{ width: '110px' }}>Fornecedor</th>
                      <th className="text-center py-3 px-3 font-semibold text-xs uppercase tracking-wider text-slate-600" style={{ width: '70px' }}>Estado</th>
                      <th className="text-center py-3 px-3 font-semibold text-xs uppercase tracking-wider text-slate-600" style={{ width: '70px' }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product) => {
                      const status = getStockStatus(product);
                      const hasPending = (product.pending_quantity || 0) > 0 || (product.cart_quantity || 0) > 0;
                      const virtualStock = product.virtual_stock ?? product.current_stock;
                      const rp = product.reorder_point ?? product.manual_reorder_point;
                      const isVirtualOk = rp != null && virtualStock >= rp;
                      
                      // Use id if available, fallback to sku for SAGE products without id
                      const productIdentifier = product.id || product.sku;
                      
                      // Calculate stock based on selected warehouses
                      let displayStock = product.current_stock;
                      if (!selectedWarehouses.includes('all') && product.stock_by_warehouse) {
                        displayStock = selectedWarehouses.reduce((sum, whCode) => {
                          return sum + (product.stock_by_warehouse[whCode] || 0);
                        }, 0);
                      }
                      
                      return (
                        <tr key={productIdentifier} className="border-b border-slate-100 hover:bg-slate-50/50">
                          <td className="py-2 lg:py-3 px-2 lg:px-3 font-mono text-xs">{product.sku}</td>
                          <td className="py-2 lg:py-3 px-2 lg:px-3" style={{ maxWidth: '280px' }}>
                            <Link 
                              to={`/inventory/${productIdentifier}`} 
                              className="hover:text-red-700 font-medium block"
                              title={product.name}
                            >
                              <span className="line-clamp-2 text-sm leading-tight">{product.name}</span>
                            </Link>
                          </td>
                          <td className="py-2 lg:py-3 px-2 lg:px-3 hidden xl:table-cell text-xs text-slate-600 truncate">{product.category || '—'}</td>
                          <td className={`py-2 lg:py-3 px-2 lg:px-3 font-mono text-right font-medium ${displayStock <= 0 ? 'text-red-600' : ''}`}>
                            {displayStock}
                          </td>
                          <td className="py-2 lg:py-3 px-2 lg:px-3 text-right">
                            {hasPending ? (
                              <div className="flex flex-col items-end">
                                {product.pending_quantity > 0 && (
                                  <span className="text-blue-600 font-mono text-xs font-medium flex items-center gap-0.5">
                                    <Truck className="h-3 w-3" />
                                    +{product.pending_quantity}
                                  </span>
                                )}
                                {product.cart_quantity > 0 && (
                                  <span className="text-purple-600 font-mono text-xs flex items-center gap-0.5">
                                    <ShoppingCart className="h-3 w-3" />
                                    +{product.cart_quantity}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className={`py-2 lg:py-3 px-2 lg:px-3 font-mono text-right font-bold ${
                            isVirtualOk ? 'text-emerald-600' : 
                            virtualStock > 0 ? 'text-orange-600' : 'text-red-600'
                          }`}>
                            {virtualStock}
                          </td>
                          <td className="py-2 lg:py-3 px-2 lg:px-3 font-mono text-right text-muted-foreground hidden xl:table-cell">{product.reorder_point ?? product.manual_reorder_point ?? '—'}</td>
                          {canViewCosts && (
                            <td className="py-2 lg:py-3 px-2 lg:px-3 font-mono text-right text-xs">
                              {product.avg_cost_price != null && product.avg_cost_price > 0
                                ? `€${Number(product.avg_cost_price).toFixed(2)}`
                                : <span className="text-slate-400">—</span>}
                            </td>
                          )}
                          <td className="py-2 lg:py-3 px-2 lg:px-3 hidden xl:table-cell text-xs text-slate-600 truncate" title={product.supplier}>{product.supplier || '—'}</td>
                          <td className="py-2 lg:py-3 px-2 lg:px-3 text-center">
                            {product.stock_status_label === 'ok' ? (
                              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs px-1.5 py-0.5">
                                <CheckCircle className="h-3 w-3" />
                              </Badge>
                            ) : product.stock_status_label === 'pending_resolution' ? (
                              <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-xs px-1.5 py-0.5">
                                <Truck className="h-3 w-3" />
                              </Badge>
                            ) : product.stock_status_label === 'out_of_stock' ? (
                              <Badge className="bg-red-100 text-red-700 border-red-200 text-xs px-1.5 py-0.5">
                                <AlertTriangle className="h-3 w-3" />
                              </Badge>
                            ) : product.stock_status_label === 'unknown' ? (
                              <Badge variant="outline" className="text-xs px-1.5 py-0.5 text-slate-500 border-slate-200" data-testid={`stock-status-unknown-${product.sku}`} title="Sem reorder point">
                                <span>—</span>
                              </Badge>
                            ) : (
                              <Badge className="bg-orange-100 text-orange-700 border-orange-200 text-xs px-1.5 py-0.5">
                                <AlertTriangle className="h-3 w-3" />
                              </Badge>
                            )}
                          </td>
                          <td className="py-2 lg:py-3 px-2 lg:px-3 text-center">
                            <div className="flex justify-center gap-1">
                              <Link to={`/inventory/${productIdentifier}`}>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`view-product-${product.sku}`}>
                                  <Edit className="h-3 w-3" />
                                </Button>
                              </Link>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                                data-testid={`delete-product-${product.sku}`}
                                onClick={() => handleDelete(productIdentifier)}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Pagination - Mobile optimized */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4 sm:mt-6">
            <p className="text-xs sm:text-sm text-muted-foreground text-center sm:text-left">
              {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalProducts)} de {totalProducts.toLocaleString()}
            </p>
            <div className="flex items-center justify-center gap-1 sm:gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1 || loading}
                data-testid="prev-page-button"
                className="h-9 px-2 sm:px-3"
              >
                <ChevronLeft className="h-4 w-4" />
                <span className="hidden sm:inline ml-1">Anterior</span>
              </Button>
              <div className="flex items-center gap-1">
                {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = idx + 1;
                  } else if (currentPage <= 3) {
                    pageNum = idx + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + idx;
                  } else {
                    pageNum = currentPage - 2 + idx;
                  }
                  return (
                    <Button
                      key={pageNum}
                      variant={currentPage === pageNum ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(pageNum)}
                      disabled={loading}
                      className={`h-9 w-9 p-0 ${currentPage === pageNum ? "bg-red-600 hover:bg-red-700" : ""}`}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages || loading}
                data-testid="next-page-button"
                className="h-9 px-2 sm:px-3"
              >
                <span className="hidden sm:inline mr-1">Próxima</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Inventory;
